/* eslint-disable react-hooks/rules-of-hooks */
import axiosRaw, { AxiosRequestConfig, AxiosResponse, ResponseType } from 'axios';
import { jwtDecode } from 'jwt-decode';
import { UserProfile } from 'types/auth';
import { markPermissionsStale } from 'auth/identity';
import { authHttp, sessionAuth } from 'auth/client';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { backend_string_2_date } from 'utils/dates';
import { FindingData } from 'pages/finding/edit';
import { queryClient } from 'utils/tanstack';

const axios = authHttp;

const login = async (data: {
  email: string;
  password: string;
  recaptcha_token_v3: string;
  recaptcha_token_v2: string;
}): Promise<UserProfile> => {
  const expected = sessionAuth.snapshot();
  const response = await axios.post('/v1/auth/customer/login', data);
  if (!response.data.success) throw new Error(response.data.message);
  const session = sessionAuth.login(response.data.data, expected);
  return jwtDecode<UserProfile>(session.token);
};

const googleLogin = async (data: { id_token: string }): Promise<UserProfile> => {
  const expected = sessionAuth.snapshot();
  const response = await axios.post('/v1/auth/login', data);
  if (!response.data.success) throw new Error(response.data.message);
  const session = sessionAuth.login(response.data.data, expected);
  return jwtDecode<UserProfile>(session.token);
};

const logout = async () => {
  const session = sessionAuth.read();
  sessionAuth.clear();
  if (!session) return;
  // Clean locally immediately, even when revocation cannot reach the server.
  const response = await axios.post('/v1/auth/logout', null, { headers: { 'x-access-token': session.token }, timeout: 15000 });
  if (!response.data.success) throw new Error(response.data.message || 'Server revocation could not be confirmed.');
};

const resetPassword = async (data: {
  code: string;
  password: string;
  confirm_password: string;
  recaptcha_token_v3: string;
  recaptcha_token_v2: string;
}) => await axios.post('/v1/auth/reset_password', data);

const forgotPassword = async (data: { email: string; recaptcha_token_v3: string; recaptcha_token_v2: string }) => {
  try {
    return await axios.post('/v1/auth/forgot_password', data);
  } catch (error) {
    if (axiosRaw.isAxiosError(error) && error.response) {
      return error.response;
    }
    throw error;
  }
};

const getOrRefreshAccessToken = async (): Promise<UserProfile> => jwtDecode<UserProfile>((await sessionAuth.access()).token);
const refreshAccessToken = async (): Promise<UserProfile> => jwtDecode<UserProfile>((await sessionAuth.refresh()).token);

export type GenericApiResponse<T = any> = {
  data: T;
  message: string;
  success: boolean;
};

const makeApiRequest = async ({
  url,
  data = '',
  method = 'POST',
  invalidate = undefined,
  responseType = 'json',
}: {
  url: string;
  data?: any;
  method?: string;
  invalidate?: string | string[];
  responseType?: ResponseType;
}): Promise<AxiosResponse<GenericApiResponse>> => {
  try {
    const response = await sessionAuth.request(token => {
      const requestConfig: AxiosRequestConfig = { url, method, headers: { 'x-access-token': token }, responseType };
      method.toUpperCase() === 'GET' ? (requestConfig.params = data) : (requestConfig.data = data);
      return axios.request(requestConfig);
    });
    if (invalidate) {
      const keys = Array.isArray(invalidate) ? invalidate : [invalidate];
      queryClient.invalidateQueries({ predicate: query => keys.some(key => String(query.queryKey[0]).startsWith(key)) });
    }
    return response;
  } catch (error) {
    // Existing consumers use the response envelope for business errors.
    if (axiosRaw.isAxiosError(error) && error.response) {
      if (error.response.status === 403) void markPermissionsStale().catch(() => {});
      return error.response;
    }
    throw error;
  }
};

export { makeApiRequest };

const changePassword = async (data: { old_password: string; new_password: string; confirm_new_password: string }) =>
  makeApiRequest({
    url: '/v1/auth/change_password',
    data,
  });

const createCompany = async (data: { legal_name: string; contacts: string; address: string; credit: number }) =>
  makeApiRequest({
    url: '/v1/company/add',
    data,
    invalidate: '/v1/company',
  });

const createPentester = async (data: { name: string; surname: string; personal_email: string; nickname: string }) =>
  makeApiRequest({
    url: '/v1/pentester/add',
    data,
    invalidate: '/v1/pentester',
  });

const createAdministrator = async (data: { email: string }) =>
  makeApiRequest({
    url: '/v1/admin/add',
    data,
    invalidate: '/v1/admin',
  });

export type BasicSearchInput =
  | {
      q?: string;
      sort_direction?: 'asc' | 'desc';
      sort_type?: string;
      page?: number;
      items_per_page?: number;
      conditions?: any;
    }
  | undefined;
export type BasicSearchOutput = { items: any[]; total_items: number; page: number; total_pages: number };

const basicList = (
  method: string,
  url: string,
  data: BasicSearchInput
): UseQueryResult<AxiosResponse<GenericApiResponse<BasicSearchOutput>>> =>
  useQuery({
    queryKey: [url, data, method],
    queryFn: () => {
      const params = {
        q: '',
        sort_direction: 'desc',
        sort_type: '',
        page: 1,
        items_per_page: 10,
        ...data,
        conditions: JSON.stringify(data?.conditions || {}),
      };
      return makeApiRequest({
        method,
        url,
        data: params,
      });
    },
    staleTime: 60 * 1000,
    enabled: !!data,
  });

const companyList = (data: BasicSearchInput): UseQueryResult<AxiosResponse<GenericApiResponse<BasicSearchOutput>>> =>
  basicList('GET', '/v1/company/list', data);

const pentestList = (data: BasicSearchInput): UseQueryResult<AxiosResponse<GenericApiResponse<BasicSearchOutput>>> =>
  basicList('GET', '/v1/pentest/list', data);

const findingList = (data: BasicSearchInput): UseQueryResult<AxiosResponse<GenericApiResponse<BasicSearchOutput>>> =>
  basicList('GET', '/v1/finding/list', data);

const pentesterList = (data: BasicSearchInput): UseQueryResult<AxiosResponse<GenericApiResponse<BasicSearchOutput>>> =>
  basicList('GET', '/v1/pentester/list', data);

const administratorList = (data: BasicSearchInput): UseQueryResult<AxiosResponse<GenericApiResponse<BasicSearchOutput>>> =>
  basicList('GET', '/v1/admin/list', data);

const pentesterAvailabilities = (data?: { q?: string }): UseQueryResult<AxiosResponse<GenericApiResponse<any>>> =>
  useQuery({
    queryKey: ['/v1/pentester/availability', data, 'GET'],
    queryFn: async () => {
      const params = {
        q: '',
        ...data,
      };
      const response = await makeApiRequest({
        method: 'GET',
        url: '/v1/pentester/availability',
        data: params,
      });

      response.data.data.forEach((pentester: any) => {
        pentester.pentests.forEach((pentest: any) => {
          pentest.start_date = pentest.start_date ? backend_string_2_date(pentest.start_date) : null;
          pentest.end_date = pentest.end_date ? backend_string_2_date(pentest.end_date) : null;
        });
      });

      return response; // Return the modified response
    },
    staleTime: 60 * 1000,
  });

type GetCompanyInput = {
  id?: number;
};

const getCompanyNoCache = async (data?: GetCompanyInput, url = '/v1/company/get'): Promise<AxiosResponse<GenericApiResponse<any>>> =>
  makeApiRequest({
    method: 'GET',
    url: url,
    data,
  });

const getCompany = (data?: GetCompanyInput, url = '/v1/company/get'): UseQueryResult<AxiosResponse<GenericApiResponse<any>>> =>
  useQuery({
    queryKey: [url, data],
    queryFn: () =>
      makeApiRequest({
        method: 'GET',
        url: url,
        data,
      }),
    staleTime: 60 * 1000,
  });

const getBuyOptions = (url = '/v1/payment/buy_options'): UseQueryResult<AxiosResponse<GenericApiResponse<any>>> =>
  useQuery({
    queryKey: [url],
    queryFn: () =>
      makeApiRequest({
        method: 'GET',
        url,
      }),
    staleTime: 60 * 1000,
  });

type GetPentestInput = {
  id?: number;
};

const getPentest2 = (data?: GetPentestInput, url = '/v1/pentest/get'): UseQueryResult<AxiosResponse<GenericApiResponse<any>>> =>
  useQuery({
    queryKey: [url, data],
    queryFn: async () => {
      const response = await makeApiRequest({
        method: 'GET',
        url,
        data,
      });

      // Check if response data is available
      if (response.data && response.data.data) {
        // Parse dates if they exist
        response.data.data.start_date = response.data.data.start_date ? backend_string_2_date(response.data.data.start_date) : null;
        response.data.data.end_date = response.data.data.end_date ? backend_string_2_date(response.data.data.end_date) : null;
      }

      return response; // Return the modified response
    },
    staleTime: 60 * 1000,
    enabled: !!data?.id && data?.id >= 0,
  });

type GetPentesterInput = {
  id?: number | undefined;
};

const getPentester2 = (data?: GetPentesterInput, url = '/v1/pentester/get'): UseQueryResult<AxiosResponse<GenericApiResponse<any>>> =>
  useQuery({
    queryKey: [url, data],
    queryFn: () =>
      makeApiRequest({
        method: 'GET',
        url,
        data,
      }),
    staleTime: 60 * 1000,
  });

type GetFindingInput = {
  id?: number;
};

type GetPentestLogsInput = {
  pentest_id?: number;
};

const getFinding2 = (data?: GetFindingInput, url = '/v1/finding/get'): UseQueryResult<AxiosResponse<GenericApiResponse<any>>> =>
  useQuery({
    queryKey: [url, data],
    queryFn: () =>
      makeApiRequest({
        method: 'GET',
        url,
        data,
      }),
    staleTime: 60 * 1000,
    enabled: !!data?.id && data?.id >= 0,
  });

const getPentestLogs = (data?: GetPentestLogsInput, url = '/v1/pentest/logs'): UseQueryResult<AxiosResponse<GenericApiResponse<any>>> =>
  useQuery({
    queryKey: [url, data],
    queryFn: () =>
      makeApiRequest({
        method: 'GET',
        url,
        data,
      }),
    staleTime: 60 * 1000,
    enabled: !!data?.pentest_id && data.pentest_id > 0,
  });

const getCompanyContract = (enable: boolean, url = '/v1/company/get_contract'): UseQueryResult<AxiosResponse<GenericApiResponse<any>>> =>
  useQuery({
    queryKey: [url],
    queryFn: () =>
      makeApiRequest({
        method: 'GET',
        url,
      }),
    staleTime: 60 * 1000,
    enabled: enable,
  });

const signCompanyContract = async () =>
  makeApiRequest({
    method: 'POST',
    url: '/v1/company/sign_contract',
    invalidate: '/v1/company',
  });

const createPentest = async (data: {
  name: string;
  description: string;
  targets: string[];
  n_endpoints: number;
  n_roles: number;
  company_id: number;
}) =>
  makeApiRequest({
    url: '/v1/pentest/add',
    data,
    invalidate: '/v1/pentest',
  });

const getPentest = async (data: { id: number }) =>
  makeApiRequest({
    url: '/v1/pentest/get',
    data,
    method: 'GET',
  });

const getFinding = async (data: { id: number; pentest_id: number }) =>
  makeApiRequest({
    url: '/v1/finding/get',
    data,
    method: 'GET',
  });

const updatePentestDraft = async (data: {
  pentest_id: number;
  name: string;
  description: string;
  targets: string[];
  n_endpoints: number;
  n_roles: number;
}) =>
  makeApiRequest({
    url: '/v1/pentest/draft/edit',
    data,
    invalidate: '/v1/pentest',
  });

const deletePentestDraft = async (data: { pentest_id: number }) =>
  makeApiRequest({
    url: '/v1/pentest/draft/delete',
    data,
    invalidate: '/v1/pentest',
  });

const updatePentester = async (data: { id: number; iban: string }) =>
  makeApiRequest({
    url: '/v1/pentester/edit',
    data,
    invalidate: '/v1/pentester',
  });

const updateCompany = async (data: { id: number; legal_name?: string; contracts?: string; address?: string; credit?: number }) =>
  makeApiRequest({
    url: '/v1/company/edit',
    data,
    invalidate: '/v1/company',
  });

const updateFinding = async (data: FindingData) =>
  makeApiRequest({
    url: '/v1/finding/edit',
    data,
    invalidate: ['/v1/finding', '/v1/pentest/logs'],
  });

const createFinding = async (data: FindingData) =>
  makeApiRequest({
    url: '/v1/finding/add',
    data,
    invalidate: '/v1/finding',
  });

const updatePentest = async (data: any) =>
  makeApiRequest({
    url: '/v1/pentest/edit',
    data: {
      ...data,
      pentesters: data.pentesters
        ? data.pentesters.map((pentester: { id: any }) => (pentester instanceof String ? pentester : pentester.id))
        : [],
    },
    invalidate: '/v1/pentest',
  });

const getSchedulePmCallLink = async (data: { pentest_id: number }) =>
  makeApiRequest({
    url: '/v1/pentest/request_schedule_call_link',
    data,
    method: 'GET',
  });

const setScheduledPmCall = async (data: { pentest_id: number; call_uri?: string }) =>
  makeApiRequest({
    url: '/v1/pentest/pm_call_scheduled',
    data,
    method: 'POST',
    invalidate: '/v1/pentest',
  });

const disablePentester = async (data: { id: number }) =>
  makeApiRequest({
    url: '/v1/pentester/disable',
    data,
    method: 'POST',
    invalidate: ['/v1/pentester', 'login-block'],
  });

const getBuyLink = async (data: { payment_package_id: string }) =>
  makeApiRequest({
    url: '/v1/payment/generate_pay_link',
    data,
    method: 'POST',
  });

const findingCategoryList = (
  url = '/v1/finding/categories/list?q=&sort_direction=ASC&sort_type=name&page=1&items_per_page=100000&conditions={}'
): UseQueryResult<AxiosResponse<GenericApiResponse<any>>> =>
  useQuery({
    queryKey: [url],
    queryFn: () =>
      makeApiRequest({
        method: 'GET',
        url,
      }),
    staleTime: 60 * 10000,
  });

const getFindingCategory = (data: { id: string }, url = '/v1/finding/template') =>
  makeApiRequest({
    url,
    data,
    method: 'GET',
  });

const getPentestTOE = async (data: { pentest_id: number }) =>
  makeApiRequest({
    url: '/v1/pentest/toe',
    data,
    method: 'GET',
  });

const payPentest = async (data: { pentest_id: number }) =>
  makeApiRequest({
    url: '/v1/pentest/pay',
    data,
    method: 'POST',
    invalidate: ['/v1/pentest', '/v1/company'],
  });

const getFileUploadUrl = async (data: { content_type: string }) =>
  makeApiRequest({
    url: '/v1/file/upload',
    data,
    method: 'GET',
  });

const getFile = async (data: { id: string }) =>
  makeApiRequest({
    url: '/v1/file/get',
    data,
    method: 'GET',
  });

const getDashboard = (url = '/v1/dashboard/customer'): UseQueryResult<AxiosResponse<GenericApiResponse<any>>> =>
  useQuery({
    queryKey: [url],
    queryFn: () =>
      makeApiRequest({
        method: 'GET',
        url,
      }),
    staleTime: 60 * 1000,
  });

const listVpnRegions = (url = '/v1/vpn/list_regions'): UseQueryResult<AxiosResponse<GenericApiResponse<any>>> =>
  useQuery({
    queryKey: [url],
    queryFn: () =>
      makeApiRequest({
        method: 'GET',
        url,
      }),
    staleTime: 60 * 1000,
  });

const generateVPNConfig = async (data: { region: string }) =>
  makeApiRequest({
    url: '/v1/vpn/generate',
    data,
    method: 'POST',
  });

const revokeVPNConfig = async (data: { region: string }) =>
  makeApiRequest({
    url: '/v1/vpn/revoke',
    data,
    method: 'POST',
  });

const reportTemplateUpsert = async (data: FormData) =>
  makeApiRequest({
    url: '/v1/report/template/upsert',
    data,
    method: 'POST',
    invalidate: '/v1/report/template/',
  });

const reportTemplateList = (data: BasicSearchInput): UseQueryResult<AxiosResponse<GenericApiResponse<BasicSearchOutput>>> =>
  basicList('GET', '/v1/report/template/list', data);

const getReportTemplate = (
  data?: { id: string | undefined },
  url = '/v1/report/template/get'
): UseQueryResult<
  AxiosResponse<
    GenericApiResponse<{
      id: number;
      name: string;
      is_global: boolean;
      company_id?: number;
    }>
  >
> =>
  useQuery({
    queryKey: [url, data],
    queryFn: () =>
      makeApiRequest({
        method: 'GET',
        url,
        data,
      }),
    staleTime: 60 * 1000,
    enabled: !!data?.id,
  });

const deleteReportTemplate = (data: { id: number }) =>
  makeApiRequest({
    url: '/v1/report/template/delete',
    data,
    method: 'POST',
    invalidate: '/v1/report/template/',
  });

const makeApiRequestFileDownload = async (data: any, url: string) =>
  sessionAuth.request(token => axios.get(url, { headers: { 'x-access-token': token }, params: data, responseType: 'blob' }));

const downloadReport = async (data: { pentest_id: number }, url = '/v1/report/download-legacy') => makeApiRequestFileDownload(data, url);

const reportTemplatePreview = (data: { id: number }, url = '/v1/report/template/preview') => makeApiRequestFileDownload(data, url);

const downloadReportV2 = async (
  data: { pentest_id: number; template_id: number; file_type: 'pdf' | 'odt' | 'docx' },
  url = '/v1/report/generate'
) => makeApiRequestFileDownload(data, url);

const reportTemplateDownload = (data: { id: number }, url = '/v1/report/template/download') => makeApiRequestFileDownload(data, url);

export default {
  login,
  logout,
  googleLogin,
  getOrRefreshAccessToken,
  refreshAccessToken,
  changePassword,
  createCompany,
  updateCompany,
  resetPassword,
  forgotPassword,
  pentestList,
  getCompanyNoCache,
  getCompany,
  getCompanyContract,
  signCompanyContract,
  createPentest,
  updatePentestDraft,
  deletePentestDraft,
  updatePentest,
  updatePentester,
  getPentest,
  getSchedulePmCallLink,
  setScheduledPmCall,
  disablePentester,
  getPentest2,
  getPentester2,
  getFinding2,
  getPentestLogs,
  companyList,
  getBuyOptions,
  getBuyLink,
  pentesterList,
  administratorList,
  pentesterAvailabilities,
  createPentester,
  findingList,
  getFinding,
  updateFinding,
  createFinding,
  findingCategoryList,
  getFindingCategory,
  downloadReport,
  createAdministrator,
  payPentest,
  getPentestTOE,
  getFileUploadUrl,
  getFile,
  getDashboard,
  listVpnRegions,
  generateVPNConfig,
  revokeVPNConfig,
  reportTemplateUpsert,
  reportTemplateList,
  reportTemplatePreview,
  reportTemplateDownload,
  getReportTemplate,
  deleteReportTemplate,
  downloadReportV2,
};
